import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { alpha } from "@material-ui/core/styles";
import { DateRangePicker, DateRangeDelimiter } from "@material-ui/pickers";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Drawer from "@material-ui/core/Drawer";
import * as Var from "../../styles/variables";
import FilterDrawer from "../../components/Filter/FilterDrawer";
import FilterDrawerNtt from "../../components/Filter/FilterDrawerNtt";
import FilterDrawerNttFt from "../../components/Filter/FilterDrawerNttFt";
import FilterDrawerHokuden from "../../components/Filter/FilterDrawerHokuden";
import FilterDrawerSaitama from "../../components/Filter/FilterDrawerSaitama";
import {
  setCurrentPageNumber,
  setFilterDrawerState,
  setFilterType,
  setDateRange,
  setMinMaxDateRange,
  getSavedSelectedTagDataByEmail,
  getHomeImageData,
  getReportData,
  getHomeImageDataNtt,
  getReportDataNtt,
  getHomeImageDataHokuden,
  getReportDataHokuden,
  getReportDataMission,
} from "../../actions";
import moment from "moment-timezone";
import { isSaitamaSeasonSelected } from "../../config/SaitamaCityConfig";
import { isJrAccessKey } from "../../config/JrConfig";

export const isJrNttPhotographyCompany = (companyName) => companyName === "jr";
export const shouldFetchNttPhotography = (companyName) =>
  companyName === "ntt" || companyName === "ntt-ft" || companyName === "jr";

export const getPhotographyDefaultDays = (companyName, accessKey = "") => {
  if (companyName === "jr" || isJrAccessKey(accessKey)) return 7;
  if (companyName === "ntt-ft" || companyName === "okiden") return 8;
  if (companyName === "hokuden") return 2;
  return 3;
};

export const buildInclusivePhotographyDateRange = ({
  startDate,
  endDate,
  nowJst,
  maxDays,
}) => {
  const now = moment(nowJst).tz("Asia/Tokyo");
  const endDay = moment.min(moment(endDate), now).tz("Asia/Tokyo").startOf("day");
  const startCandidate = endDay.clone().subtract(maxDays - 1, "days");
  const startDay = moment.max(
    moment(startDate).tz("Asia/Tokyo").startOf("day"),
    startCandidate
  );
  return [startDay, endDay];
};

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      backgroundColor: alpha("#fafafa", 1),
      marginTop: theme.spacing(-2),
      zIndex: 1000,
      paddingTop: 0,
      paddingLeft: 8,
      marginTop: -12,
    },
    container: {
      display: "flex",
      width: 300,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      alignItems: "baseline",
    },
    calendarSection: {
      marginTop: "10px",
    },
    delimiter: {
      paddingTop: theme.spacing(1),
    },
    dataBtn: {
      marginTop: theme.spacing(1.2),
      marginLeft: theme.spacing(1),
      height: theme.spacing(4),
      paddingTop: theme.spacing(1.5),
      color: Var.btnLabelColor,
    },
    sectionCalendar: {
      display: "flex",
    },
    filterBtn: {
      marginTop: theme.spacing(1.2),
      height: theme.spacing(4),
      paddingTop: theme.spacing(1.5),
    },
  })
);

const Calendar = ({
  companyName,
  currentPage,
  setDateRange,
  dateRange,
  minMaxDateRange,
  setCurrentPageNumber,
  getSavedSelectedTagDataByEmail,
  getHomeImageData,
  setFilterDrawerState,
  periodType,
  seasonData,
  missionData,
  selectedSeason,
  setMinMaxDateRange,
  selectedMission,
  filterStore,
  filterType,
  setFilterType,
  getReportData,
  getHomeImageDataNtt,
  getReportDataNtt,
  getHomeImageDataHokuden,
  getReportDataHokuden,
  getReportDataMission,
}) => {
  const anchor = "right";
  const classes = useStyles();
  const [error, setError] = useState([null, null]);
  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });
  const [isFutureOnly, setIsFutureOnly] = useState(false);
  const isMitsubishi = companyName === "mitsubishi";
  const isSaitamaPhotography = isSaitamaSeasonSelected(
    localStorage.getItem("access_key"),
    selectedSeason
  );
  const isJr = isJrAccessKey(localStorage.getItem("access_key"));
  const isDisplayDisabled =
    (periodType === "season" &&
      (selectedSeason === "blank" || !selectedSeason)) ||
    (periodType !== "season" &&
      (selectedMission === "blank" || !selectedMission)) ||
    (isJr && (error.some(Boolean) || !dateRange?.[0] || !dateRange?.[1]));

  const toJstMidnightDate = (input) => {
    if (!input) return null;
    const m = moment.isMoment(input) ? input.clone() : moment(input);
    const ymd = m.tz("Asia/Tokyo").format("YYYY-MM-DD");
    return moment
      .tz(ymd, "YYYY-MM-DD", "Asia/Tokyo")
      .hour(15) // ← 12でもOK。DST含め安全寄りなら15
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate();
  };

  useEffect(() => {
    if (currentPage.includes("home")) {
      setFilterType("photo");
    } else {
      setFilterType("report");
    }
  }, [currentPage]);

  useEffect(() => {
    //const nowJstString = moment().tz("Asia/Tokyo").format("YYYY-MM-DDTHH:mm:ss");
    const nowJst = moment().tz("Asia/Tokyo");
    const maxDays = getPhotographyDefaultDays(
      companyName,
      localStorage.getItem("access_key") || ""
    );

    if (periodType === "season") {
      const selectedSeasonData = seasonData.find(
        (season) => season.seasonId === selectedSeason
      );
      if (selectedSeasonData) {
        const seasonStart = moment.tz(selectedSeasonData.seasonStart, "Asia/Tokyo");
        const seasonEnd   = moment.tz(selectedSeasonData.seasonEnd, "Asia/Tokyo");

        // 将来シーズン開始のときは開始日＝終了日＝seasonStartに固定
        if (seasonStart.isAfter(nowJst)) {
          const seasonStartDay = seasonStart.clone().tz("Asia/Tokyo").startOf("day");
          setDateRange([toJstMidnightDate(seasonStartDay), toJstMidnightDate(seasonStartDay)]);
          setMinMaxDateRange([toJstMidnightDate(seasonStartDay), toJstMidnightDate(seasonStartDay)]);
          setIsFutureOnly(true);
          return;
        }

        const [startDay, endDay] = buildInclusivePhotographyDateRange({
          startDate: seasonStart,
          endDate: seasonEnd,
          nowJst,
          maxDays,
        });
        const seasonStartDay = seasonStart.clone().tz("Asia/Tokyo").startOf("day");

        setDateRange([toJstMidnightDate(startDay), toJstMidnightDate(endDay)]);
        setMinMaxDateRange([toJstMidnightDate(seasonStartDay), toJstMidnightDate(endDay)]);

        const todayJst = moment().tz("Asia/Tokyo").startOf("day");
        const isStartFuture = seasonStart.isAfter(todayJst);
        const isEndFuture = seasonEnd.isAfter(todayJst);

        setIsFutureOnly(isStartFuture && isEndFuture);
      }
    } else {
      // Mission
      const selectedMissionData = missionData.find(
        (mission) => mission.missionId === selectedMission
      );
      if (selectedMissionData) {
        const missionStart = moment.tz(selectedMissionData.missionStart, "Asia/Tokyo");
        const missionEnd   = moment.tz(selectedMissionData.missionEnd, "Asia/Tokyo");

        const [startDay, endDay] = buildInclusivePhotographyDateRange({
          startDate: missionStart,
          endDate: missionEnd,
          nowJst,
          maxDays,
        });
        const missionStartDay = missionStart.clone().tz("Asia/Tokyo").startOf("day");

        setDateRange([toJstMidnightDate(startDay), toJstMidnightDate(endDay)]);
        setMinMaxDateRange([toJstMidnightDate(missionStartDay), toJstMidnightDate(endDay)]);
      }
    }
  }, [selectedSeason, selectedMission]);

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setState({ ...state, [anchor]: open });
    setFilterDrawerState(open);
  };

  // 月移動時にPicker内のフォーカスを維持する。
  // 月移動ボタンがフォーカスを取得すると、Material-UIのPickerがblurで閉じるため。
  const preserveDatePickerFocus = (event) => {
    event.preventDefault();
  };

  const fetchData = () => {
    setCurrentPageNumber(1);
    if (filterStore.tag.length > 0) {
      getSavedSelectedTagDataByEmail();
    } else {
      if (currentPage.includes("report")) {
        if (isSaitamaPhotography) {
          getReportData({ pageNumber: 1 });
        } else if (shouldFetchNttPhotography(companyName)) {
          getReportDataNtt({
            pageNumber: 1,
          });
        } else if (companyName === "okiden") {
          getReportDataNtt({
            pageNumber: 1,
          });
        } else if (companyName === "hokuden") {
          getReportDataHokuden({
            pageNumber: 1,
          });
        } else if (companyName === "tepco" || companyName === "common" || companyName === "saitama-city") {
          getReportData({
            pageNumber: 1,
          });
        } else if (companyName === "mitsubishi") {
          getReportDataMission({
            pageNumber: 1,
          });
        }
      } else {
        if (isSaitamaPhotography) {
          getHomeImageData({ pageNumber: 1 });
        } else if (companyName === "ntt" || companyName === "ntt-ft" || companyName === "jr") {
          getHomeImageDataNtt({
            pageNumber: 1,
          });
        } else if (companyName === "okiden") {
          getHomeImageDataNtt({
            pageNumber: 1,
          });
        } else if (companyName === "hokuden") {
          getHomeImageDataHokuden({
            pageNumber: 1,
          });
        } else if (companyName === "tepco" || companyName === "saitama-city" || companyName === "mitsubishi") {
          getHomeImageData({
            pageNumber: 1,
          });
        }
      }
    }
  };

  const renderContent = () => {
    if (companyName === "saitama-city" || isSaitamaPhotography) return (
      <FilterDrawerSaitama
        toggleDrawer={toggleDrawer}
        anchor={anchor}
        filterType={filterType}
      />
    );
    if (companyName === "tepco" || companyName === "common") return (
      <FilterDrawer
        toggleDrawer={toggleDrawer}
        anchor={anchor}
        filterType={filterType}
      />
    );
    if (companyName === "ntt" || companyName === "jr") return (
      <FilterDrawerNtt
        toggleDrawer={toggleDrawer}
        anchor={anchor}
        filterType={filterType}
      />
    );
    if (companyName === "ntt-ft" || companyName === "okiden") return (
      <FilterDrawerNttFt
        toggleDrawer={toggleDrawer}
        anchor={anchor}
        filterType={filterType}
      />
    );
    if (companyName === "hokuden") return (
      <FilterDrawerHokuden
        toggleDrawer={toggleDrawer}
        anchor={anchor}
        filterType={filterType}
      />
    );
    return null;
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={3} direction="row">
        <Grid item>
          <DateRangePicker
            startText="開始"
            endText="終了"
            disableHighlightToday={true}
            showToolbar={false}
            allowSameDateSelection={true}
            leftArrowButtonProps={{ onMouseDown: preserveDatePickerFocus }}
            rightArrowButtonProps={{ onMouseDown: preserveDatePickerFocus }}
            minDate={minMaxDateRange[0] !== null ? minMaxDateRange[0] : null}
            maxDate={minMaxDateRange[1] !== null ? minMaxDateRange[1] : null}
            value={dateRange}
            onChange={(newValue) => {
              const [start, end] = newValue || [];
              setDateRange([toJstMidnightDate(start), toJstMidnightDate(end)]);
            }}
            onError={([startReason, endReason], [start, end]) => {
              if (startReason === "invalidRange") {
                setError([null, null]);
                return;
              }
              setError([startReason, endReason]);
            }}
            renderInput={(startProps, endProps) => (
              <div className={classes.container}>
                <TextField
                  id="standard-basic"
                  {...startProps}
                  error={Boolean(error[0])}
                  size="small"
                  helperText=""
                />
                <DateRangeDelimiter>
                  <span className={classes.delimiter}>~</span>
                </DateRangeDelimiter>
                <TextField
                  id="standard-basic"
                  {...endProps}
                  error={Boolean(error[0])}
                  size="small"
                  helperText=""
                />
              </div>
            )}
          />
        </Grid>
        <Grid item>
          <Button
            className={classes.dataBtn}
            variant="contained"
            color="primary"
            onClick={() => fetchData()}
            disabled={isDisplayDisabled}
          >
            表示
          </Button>
        </Grid>
        {!isMitsubishi && (
          <Grid item>
            <Button
              className={classes.filterBtn}
              variant="contained"
              color="default"
              onClick={toggleDrawer(anchor, true)}
              disabled={isDisplayDisabled}
            >
              絞り込み
            </Button>
            <Drawer
              anchor={anchor}
              open={state[anchor]}
              onClose={toggleDrawer(anchor, false)}
            >
              {renderContent()}
            </Drawer>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

Calendar.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  currentPage: state.app.currentPage,
  dateRange: state.app.dateRange,
  minMaxDateRange: state.app.minMaxDateRange,
  filterDrawerState: state.app.filterDrawerState,
  periodType: state.app.periodType,
  seasonData: state.app.seasonData,
  missionData: state.app.missionData,
  selectedSeason: state.app.selectedSeason,
  selectedMission: state.app.selectedMission,
  filterStore: state.home.filter,
  filterType: state.app.filterType,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentPageNumber: (param) => dispatch(setCurrentPageNumber(param)),
  setDateRange: (param) => dispatch(setDateRange(param)),
  setMinMaxDateRange: (param) => dispatch(setMinMaxDateRange(param)),
  setFilterDrawerState: (param) => dispatch(setFilterDrawerState(param)),
  setFilterType: (param) => dispatch(setFilterType(param)),
  getSavedSelectedTagDataByEmail: () => dispatch(getSavedSelectedTagDataByEmail()),
  getHomeImageData: (param) => dispatch(getHomeImageData(param)),
  getReportData: (param) => dispatch(getReportData(param)),
  getHomeImageDataNtt: (param) => dispatch(getHomeImageDataNtt(param)),
  getReportDataNtt: (param) => dispatch(getReportDataNtt(param)),
  getHomeImageDataHokuden: (param) => dispatch(getHomeImageDataHokuden(param)),
  getReportDataHokuden: (param) => dispatch(getReportDataHokuden(param)),
  getReportDataMission: (param) => dispatch(getReportDataMission(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(Calendar));
